import emojis from "@/assets/emojis";
import { Button } from "@/components/ui/button";
import BannerData, { BannerType } from "@/domain/entities/BannerData";
import { NOTIFICATION_BAR_HEIGHT_PX } from "@/lib/layoutConstants";
import { cn } from "@/lib/utils";
import { Info, X } from "lucide-react";

interface NotificationBarProps {
    bannerData: BannerData;
    isVisible: boolean;
    setHideNotificationBar: (hideNotificationBar: boolean) => void;
}

const NotificationBar = ({
    bannerData,
    isVisible,
    setHideNotificationBar,
}: NotificationBarProps) => {
    const isTokenExhausted = bannerData.type === BannerType.ERROR;
    const isTrialBanner =
        !bannerData.type || bannerData.type === BannerType.INFO;

    return (
        <div
            className={cn(
                "flex h-14 w-full items-center justify-between pl-11 pr-5 transition-all duration-1000",
                !isVisible && "h-0",
                isTrialBanner && "bg-banner-trial text-neutral-darkest",
                isTokenExhausted &&
                    "bg-banner-tokenExhausted text-neutral-white",
                bannerData.type === BannerType.WARNING &&
                    "bg-warning-base text-neutral-darkest",
                bannerData.type === BannerType.SUCCESS &&
                    "bg-success-base text-neutral-darkest",
            )}
            onClick={(e) => {
                e.stopPropagation();
                if (bannerData.linkUrl) {
                    window.open(bannerData.linkUrl, "_blank");
                }
            }}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter" || e.key === " ") {
                    if (bannerData.linkUrl) {
                        window.open(bannerData.linkUrl, "_blank");
                    }
                }
            }}
            role="button"
            style={{
                height: isVisible ? `${NOTIFICATION_BAR_HEIGHT_PX}px` : 0,
            }}
            tabIndex={0}
        >
            <div className="flex min-w-0 flex-1 items-center gap-4">
                {isTokenExhausted ? (
                    <Info
                        className="h-5 w-5 shrink-0 text-neutral-white"
                        strokeWidth={2}
                        aria-hidden
                    />
                ) : (
                    bannerData.icon && (
                        <p className="shrink-0">
                            {
                                emojis.find(
                                    (emoji) => emoji.name === bannerData.icon,
                                )?.symbol
                            }
                        </p>
                    )
                )}
                <div
                    className={cn(
                        "flex min-w-0 flex-col leading-[1.35]",
                        isTokenExhausted && "flex-row items-center",
                    )}
                >
                    {bannerData.title && !isTokenExhausted && (
                        <p className="font-semibold">{bannerData.title}</p>
                    )}
                    {bannerData.mainText && (
                        <p
                            className={cn(
                                "text-sm",
                                isTokenExhausted && "font-body text-sm",
                            )}
                        >
                            {bannerData.mainText}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex shrink-0 items-center justify-center gap-8">
                {bannerData.callToActionText &&
                    bannerData.callToActionCallback && (
                        <Button
                            className={cn(
                                "border-primary bg-transparent underline hover:text-neutral-darkest",
                                isTokenExhausted &&
                                    "border-neutral-white text-neutral-white hover:bg-white/10 hover:text-neutral-white",
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                bannerData.callToActionCallback?.();
                            }}
                            variant="outline"
                        >
                            {bannerData.callToActionText}
                        </Button>
                    )}
                {bannerData.dismissible && (
                    <Button
                        className={cn(
                            isTokenExhausted &&
                                "text-neutral-white hover:bg-white/10 hover:text-neutral-white",
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            setHideNotificationBar(true);
                        }}
                        size="tinyCircle"
                        variant="ghost"
                    >
                        <X
                            className={cn(
                                "h-3 w-3",
                                isTokenExhausted && "text-neutral-white",
                            )}
                        />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default NotificationBar;
